'use client'

import ConfirmModal from '@/components/confirm-modal'
import HealthMapQuestionModal from '@/components/health-map/health-map-question-modal'
import {
    createHealthMapCategory,
    createHealthMapTheme,
    deleteHealthMapCategory,
    deleteHealthMapQuestion,
    deleteHealthMapTheme,
    getHealthMapStructure,
    HealthMapCategory,
    HealthMapQuestion,
    HealthMapTheme,
    HEALTH_MAP_QUESTION_TYPE_LABELS,
    updateHealthMapCategory,
    updateHealthMapQuestion,
    updateHealthMapTheme,
} from '@/lib/api/health-map/health-map-api'
import { useEffect, useState } from 'react'
import {
    MdAdd,
    MdArrowBack,
    MdDelete,
    MdEdit,
    MdExpandLess,
    MdExpandMore,
    MdLock,
    MdMonitorHeart,
    MdVisibility,
    MdVisibilityOff,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

type PendingDeletion =
    | { kind: 'category'; id: number; name: string }
    | { kind: 'theme'; id: number; name: string }
    | { kind: 'question'; id: number; name: string }
    | null

/**
 * Montagem do questionário do Mapa da Saúde: categorias, temas e perguntas.
 *
 * O que é do sistema aparece com cadeado e não pode ser editado nem excluído — é
 * a base clínica comum a todas as contas, e reescrevê-la faria dois prontuários
 * com o mesmo enunciado deixarem de significar a mesma coisa. Desativar também
 * não: o que a organização pode fazer é acrescentar o que falta.
 */
export default function HealthMapSettingsPage() {
    const navigate = useNavigate()

    const [categories, setCategories] = useState<HealthMapCategory[]>([])
    const [loading, setLoading] = useState(true)
    const [expanded, setExpanded] = useState<number[]>([])

    const [newCategoryName, setNewCategoryName] = useState('')
    const [creatingCategory, setCreatingCategory] = useState(false)
    const [themeDraft, setThemeDraft] = useState<Record<number, string>>({})
    const [questionTarget, setQuestionTarget] = useState<{
        theme: HealthMapTheme
        question?: HealthMapQuestion | null
    } | null>(null)
    const [pendingDeletion, setPendingDeletion] = useState<PendingDeletion>(null)
    const [deleting, setDeleting] = useState(false)

    useEffect(() => {
        loadStructure()
    }, [])

    const loadStructure = async () => {
        setLoading(true)
        try {
            // `includeInactive`: aqui é preciso ver o que foi desativado para
            // reativar — o preenchimento nunca traz isso.
            const result = await getHealthMapStructure(true)
            if (result.success) {
                setCategories(result.result || [])
            } else {
                toast.error(result.error || 'Erro ao carregar o questionário')
            }
        } finally {
            setLoading(false)
        }
    }

    const toggleExpanded = (categoryId: number) => {
        setExpanded(previous =>
            previous.includes(categoryId)
                ? previous.filter(id => id !== categoryId)
                : [...previous, categoryId]
        )
    }

    const handleCreateCategory = async () => {
        if (!newCategoryName.trim()) return
        setCreatingCategory(true)
        try {
            const result = await createHealthMapCategory({
                name: newCategoryName.trim(),
            })
            if (result.success) {
                toast.success('Categoria criada')
                setNewCategoryName('')
                await loadStructure()
            } else {
                toast.error(result.error || 'Erro ao criar categoria')
            }
        } finally {
            setCreatingCategory(false)
        }
    }

    const handleCreateTheme = async (categoryId: number) => {
        const name = (themeDraft[categoryId] || '').trim()
        if (!name) return

        const result = await createHealthMapTheme({ categoryId, name })
        if (result.success) {
            toast.success('Tema criado')
            setThemeDraft(previous => ({ ...previous, [categoryId]: '' }))
            await loadStructure()
        } else {
            toast.error(result.error || 'Erro ao criar tema')
        }
    }

    const handleToggleActive = async (
        kind: 'category' | 'theme' | 'question',
        id: number,
        active: boolean
    ) => {
        const call =
            kind === 'category'
                ? updateHealthMapCategory(id, { active })
                : kind === 'theme'
                  ? updateHealthMapTheme(id, { active })
                  : updateHealthMapQuestion(id, { active })

        const result = await call
        if (result.success) {
            await loadStructure()
        } else {
            toast.error(result.error || 'Não foi possível alterar')
        }
    }

    const confirmDeletion = async () => {
        if (!pendingDeletion) return
        setDeleting(true)
        try {
            const result =
                pendingDeletion.kind === 'category'
                    ? await deleteHealthMapCategory(pendingDeletion.id)
                    : pendingDeletion.kind === 'theme'
                      ? await deleteHealthMapTheme(pendingDeletion.id)
                      : await deleteHealthMapQuestion(pendingDeletion.id)

            if (result.success) {
                toast.success('Excluído')
                await loadStructure()
            } else {
                toast.error(result.error || 'Não foi possível excluir')
            }
        } finally {
            setDeleting(false)
            setPendingDeletion(null)
        }
    }

    if (loading) {
        return (
            <div className="flex min-h-[400px] flex-col items-center justify-center gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-sm text-base-content/60">
                    Carregando o questionário...
                </p>
            </div>
        )
    }

    return (
        <div className="mx-auto flex max-w-5xl flex-col gap-6 p-4">
            {/* Cabeçalho */}
            <div className="flex items-center gap-3">
                <button
                    onClick={() => navigate(-1)}
                    className="btn btn-ghost btn-circle btn-sm"
                    aria-label="Voltar"
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-secondary/10">
                        <MdMonitorHeart className="text-2xl text-secondary" />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold text-base-content">
                            Mapa da Saúde
                        </h1>
                        <p className="text-sm text-base-content/60">
                            Categorias, temas e perguntas do questionário
                        </p>
                    </div>
                </div>
            </div>

            {/* Nova categoria */}
            <div className="rounded-xl border border-base-300/50 bg-base-100 p-4">
                <label className="mb-2 block text-sm font-medium">
                    Nova categoria
                </label>
                <div className="flex items-stretch gap-2">
                    <input
                        type="text"
                        className="input input-bordered flex-1"
                        placeholder="Ex.: Saúde da Mente"
                        value={newCategoryName}
                        onChange={event => setNewCategoryName(event.target.value)}
                        onKeyDown={event => {
                            if (event.key === 'Enter') handleCreateCategory()
                        }}
                    />
                    <button
                        type="button"
                        className="btn btn-secondary h-auto min-h-0 gap-1"
                        onClick={handleCreateCategory}
                        disabled={creatingCategory || !newCategoryName.trim()}
                    >
                        {creatingCategory ? (
                            <span className="loading loading-spinner loading-xs" />
                        ) : (
                            <MdAdd className="text-base" />
                        )}
                        Criar
                    </button>
                </div>
            </div>

            {/* Árvore */}
            {categories.length === 0 ? (
                <div className="rounded-xl border border-base-300/50 bg-base-100 p-8 text-center">
                    <MdMonitorHeart className="mx-auto mb-3 text-4xl text-base-content/20" />
                    <p className="text-base-content/60">
                        Nenhuma categoria cadastrada ainda.
                    </p>
                </div>
            ) : (
                <div className="flex flex-col gap-3">
                    {categories.map(category => {
                        const isOpen = expanded.includes(category.id)
                        return (
                            <div
                                key={category.id}
                                className={`rounded-xl border bg-base-100 ${
                                    category.active
                                        ? 'border-base-300/50'
                                        : 'border-base-300/50 opacity-60'
                                }`}
                            >
                                <div className="flex items-center gap-2 p-4">
                                    <button
                                        type="button"
                                        className="btn btn-ghost btn-sm btn-square"
                                        onClick={() =>
                                            toggleExpanded(category.id)
                                        }
                                        aria-expanded={isOpen}
                                    >
                                        {isOpen ? (
                                            <MdExpandLess className="text-lg" />
                                        ) : (
                                            <MdExpandMore className="text-lg" />
                                        )}
                                    </button>
                                    <div className="min-w-0 flex-1">
                                        <p className="flex items-center gap-2 font-semibold text-base-content">
                                            {category.name}
                                            {category.isSystem && (
                                                <span
                                                    className="badge badge-ghost badge-sm gap-1"
                                                    title="Categoria padrão do sistema"
                                                >
                                                    <MdLock className="text-xs" />
                                                    Padrão
                                                </span>
                                            )}
                                        </p>
                                        <p className="text-xs text-base-content/50">
                                            {category.themes.length}{' '}
                                            {category.themes.length === 1
                                                ? 'tema'
                                                : 'temas'}
                                        </p>
                                    </div>
                                    {!category.isSystem && (
                                        <>
                                            <button
                                                type="button"
                                                className="btn btn-ghost btn-sm btn-square"
                                                title={
                                                    category.active
                                                        ? 'Desativar'
                                                        : 'Ativar'
                                                }
                                                onClick={() =>
                                                    handleToggleActive(
                                                        'category',
                                                        category.id,
                                                        !category.active
                                                    )
                                                }
                                            >
                                                {category.active ? (
                                                    <MdVisibility className="text-base" />
                                                ) : (
                                                    <MdVisibilityOff className="text-base" />
                                                )}
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-ghost btn-sm btn-square text-error"
                                                title="Excluir categoria"
                                                onClick={() =>
                                                    setPendingDeletion({
                                                        kind: 'category',
                                                        id: category.id,
                                                        name: category.name,
                                                    })
                                                }
                                            >
                                                <MdDelete className="text-base" />
                                            </button>
                                        </>
                                    )}
                                </div>

                                {isOpen && (
                                    <div className="flex flex-col gap-3 border-t border-base-300/50 p-4">
                                        {category.themes.map(theme => (
                                            <div
                                                key={theme.id}
                                                className={`rounded-lg border border-base-300/50 ${
                                                    theme.active
                                                        ? ''
                                                        : 'opacity-60'
                                                }`}
                                            >
                                                <div className="flex items-center gap-2 border-b border-base-300/50 bg-base-200/30 px-4 py-3">
                                                    <div className="min-w-0 flex-1">
                                                        <p className="flex items-center gap-2 text-sm font-semibold">
                                                            {theme.name}
                                                            {theme.isSystem && (
                                                                <MdLock
                                                                    className="text-xs text-base-content/40"
                                                                    title="Tema padrão do sistema"
                                                                />
                                                            )}
                                                        </p>
                                                        <p className="text-xs text-base-content/50">
                                                            {
                                                                theme.questions
                                                                    .length
                                                            }{' '}
                                                            {theme.questions
                                                                .length === 1
                                                                ? 'pergunta'
                                                                : 'perguntas'}
                                                        </p>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="btn btn-ghost btn-sm gap-1"
                                                        onClick={() =>
                                                            setQuestionTarget({
                                                                theme,
                                                            })
                                                        }
                                                    >
                                                        <MdAdd className="text-base" />
                                                        Pergunta
                                                    </button>
                                                    {!theme.isSystem && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-ghost btn-sm btn-square text-error"
                                                            title="Excluir tema"
                                                            onClick={() =>
                                                                setPendingDeletion(
                                                                    {
                                                                        kind: 'theme',
                                                                        id: theme.id,
                                                                        name: theme.name,
                                                                    }
                                                                )
                                                            }
                                                        >
                                                            <MdDelete className="text-base" />
                                                        </button>
                                                    )}
                                                </div>

                                                <div className="flex flex-col divide-y divide-base-300/40">
                                                    {theme.questions.length ===
                                                    0 ? (
                                                        <p className="px-4 py-3 text-sm text-base-content/50">
                                                            Nenhuma pergunta
                                                            neste tema.
                                                        </p>
                                                    ) : (
                                                        theme.questions.map(
                                                            question => (
                                                                <div
                                                                    key={
                                                                        question.id
                                                                    }
                                                                    className={`flex items-center gap-2 px-4 py-3 ${
                                                                        question.active
                                                                            ? ''
                                                                            : 'opacity-60'
                                                                    }`}
                                                                >
                                                                    <div className="min-w-0 flex-1">
                                                                        <p className="text-sm">
                                                                            {
                                                                                question.title
                                                                            }
                                                                            {question.required && (
                                                                                <span className="ml-1 text-error">
                                                                                    *
                                                                                </span>
                                                                            )}
                                                                        </p>
                                                                        <p className="flex items-center gap-2 text-xs text-base-content/50">
                                                                            {
                                                                                HEALTH_MAP_QUESTION_TYPE_LABELS[
                                                                                    question
                                                                                        .type
                                                                                ]
                                                                            }
                                                                            {question.isSystem && (
                                                                                <span className="flex items-center gap-1">
                                                                                    <MdLock className="text-xs" />
                                                                                    padrão
                                                                                </span>
                                                                            )}
                                                                        </p>
                                                                    </div>
                                                                    {!question.isSystem && (
                                                                        <>
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-ghost btn-sm btn-square"
                                                                                title="Editar"
                                                                                onClick={() =>
                                                                                    setQuestionTarget(
                                                                                        {
                                                                                            theme,
                                                                                            question,
                                                                                        }
                                                                                    )
                                                                                }
                                                                            >
                                                                                <MdEdit className="text-base" />
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-ghost btn-sm btn-square"
                                                                                title={
                                                                                    question.active
                                                                                        ? 'Desativar'
                                                                                        : 'Ativar'
                                                                                }
                                                                                onClick={() =>
                                                                                    handleToggleActive(
                                                                                        'question',
                                                                                        question.id,
                                                                                        !question.active
                                                                                    )
                                                                                }
                                                                            >
                                                                                {question.active ? (
                                                                                    <MdVisibility className="text-base" />
                                                                                ) : (
                                                                                    <MdVisibilityOff className="text-base" />
                                                                                )}
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-ghost btn-sm btn-square text-error"
                                                                                title="Excluir"
                                                                                onClick={() =>
                                                                                    setPendingDeletion(
                                                                                        {
                                                                                            kind: 'question',
                                                                                            id: question.id,
                                                                                            name: question.title,
                                                                                        }
                                                                                    )
                                                                                }
                                                                            >
                                                                                <MdDelete className="text-base" />
                                                                            </button>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            )
                                                        )
                                                    )}
                                                </div>
                                            </div>
                                        ))}

                                        {/* Novo tema */}
                                        <div className="flex items-stretch gap-2">
                                            <input
                                                type="text"
                                                className="input input-bordered input-sm flex-1"
                                                placeholder="Novo tema (ex.: Estresse)"
                                                value={
                                                    themeDraft[category.id] || ''
                                                }
                                                onChange={event =>
                                                    setThemeDraft(previous => ({
                                                        ...previous,
                                                        [category.id]:
                                                            event.target.value,
                                                    }))
                                                }
                                                onKeyDown={event => {
                                                    if (event.key === 'Enter') {
                                                        handleCreateTheme(
                                                            category.id
                                                        )
                                                    }
                                                }}
                                            />
                                            <button
                                                type="button"
                                                className="btn btn-outline btn-sm h-auto min-h-0 gap-1"
                                                onClick={() =>
                                                    handleCreateTheme(
                                                        category.id
                                                    )
                                                }
                                                disabled={
                                                    !(
                                                        themeDraft[category.id] ||
                                                        ''
                                                    ).trim()
                                                }
                                            >
                                                <MdAdd className="text-base" />
                                                Adicionar tema
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            )}

            {questionTarget && (
                <HealthMapQuestionModal
                    themeId={questionTarget.theme.id}
                    themeName={questionTarget.theme.name}
                    question={questionTarget.question}
                    onClose={() => setQuestionTarget(null)}
                    onSaved={() => loadStructure()}
                />
            )}

            <ConfirmModal
                isOpen={pendingDeletion !== null}
                onClose={() => setPendingDeletion(null)}
                onConfirm={confirmDeletion}
                loading={deleting}
                title="Excluir"
                message={`Tem certeza de que deseja excluir "${
                    pendingDeletion?.name ?? ''
                }"? As respostas já registradas continuam no prontuário dos pacientes.`}
                confirmButtonText="Excluir"
            />
        </div>
    )
}
